// Anzeige der Übergaben in der Hausmeister-App (Portale-Redesign E3). Reine Funktionen über den
// Protokollfeldern – Termin = „Datum / Uhrzeit“ aus Schritt 1 (durchgefuehrt_am, Entscheidung
// 22.10.2026). Einen Fortschrittsbalken gibt es bewusst nicht: der Assistent speichert keinen
// Schritt; gezeigt wird, was tatsächlich erfasst ist.
import type { Partei, Protokoll } from '../uebergabe/typen';

export type UebergabeInhalt = Partial<Pick<Protokoll, 'parteien' | 'raeume' | 'zaehler' | 'unterschriften' | 'bemerkungen'>>;

export type UebergabeStatus = 'geplant' | 'in_erfassung' | 'abschluss_offen';

/** „Geplant“ = noch nichts vor Ort erfasst (Räume, Zählerstände, Unterschriften, Bemerkungen) und kein Entwurf auf dem Gerät. */
export function istBegonnen(i: UebergabeInhalt, entwurfAufGeraet: boolean): boolean {
  return entwurfAufGeraet
    || (i.raeume?.length ?? 0) > 0
    || (i.unterschriften?.length ?? 0) > 0
    || !!i.bemerkungen?.trim()
    || (i.zaehler ?? []).some((z) => !!z.stand?.trim());
}

export function uebergabeStatus(abschlussOffen: boolean, begonnen: boolean): UebergabeStatus {
  if (abschlussOffen) return 'abschluss_offen';
  return begonnen ? 'in_erfassung' : 'geplant';
}

export const STATUS_TEXT: Record<UebergabeStatus, string> = {
  geplant: 'Geplant',
  in_erfassung: 'In Erfassung',
  abschluss_offen: 'Abschluss offen',
};

/** „Übergeber → Übernehmer“ (ohne Zeugen, leere Namen weggelassen). */
export function parteienText(parteien: Pick<Partei, 'rolle' | 'name'>[] | undefined): string {
  const name = (rolle: Partei['rolle']) => (parteien ?? []).find((p) => p.rolle === rolle)?.name?.trim() || '';
  return [name('uebergeber'), name('uebernehmer')].filter(Boolean).join(' → ');
}

/** Kurzfassung des Erfassten, z. B. „3 Räume · 2/4 Zähler · 1/2 Unterschriften“. */
export function erfasstText(i: UebergabeInhalt): string {
  const teile: string[] = [];
  const raeume = i.raeume?.length ?? 0;
  if (raeume) teile.push(raeume === 1 ? '1 Raum' : `${raeume} Räume`);
  const zaehler = i.zaehler ?? [];
  const mitStand = zaehler.filter((z) => !!z.stand?.trim()).length;
  if (mitStand) teile.push(`${mitStand}/${zaehler.length} Zähler`);
  const noetig = (i.parteien ?? []).filter((p) => p.rolle !== 'zeuge').length;
  const sig = i.unterschriften?.length ?? 0;
  if (sig) teile.push(noetig ? `${Math.min(sig, noetig)}/${noetig} Unterschriften` : `${sig} Unterschriften`);
  return teile.length ? teile.join(' · ') : 'Noch nichts erfasst';
}

/** „Do., 22.10., 09:30 Uhr“ */
export function terminText(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
}

/** Kalenderblatt: „Okt“ / „22“ */
export function kalenderblatt(iso: string | null | undefined): { monat: string; tag: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { monat: d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', ''), tag: String(d.getDate()) };
}
